import React, { ReactNode, useContext, useEffect, useMemo } from 'react';
import qs from 'qs';
import { TextVariant } from '@naf/text';
import { useDispatch } from 'react-redux';
import { Message } from '@naf/input';
import { Link } from '@naf-simba/elements-links';
import { useNavigate, useLocation } from 'react-router-dom';
import { getURLParams } from '@lib/getUrlParams';
import { LocationSlugs } from '@appTypes/nafLocations';
import { actions } from '@redux/modules/nafEvents';
import useSelector from '@redux/typedHooks';
import { Spinner } from '../../../components/spinner/Spinner';
import { useGetNafLocations } from '../../../hooks/useGetNafLocations';
import EventList from '../components/EventList/EventList';
import EventFilter from '../components/EventFilter';
import { InternalSearchCardType } from '../../../components/layout/InternalSearchMetaData';
import { LayoutWithMainContent } from '../../../components/layout/Layout';
import { NafLocationsAppContext } from '../NafLocationsApp';
import { useWindowLocation } from '../../../hooks/Window/useWindowLocation';
import LocationChips from '../components/LocationChips';
import * as S from './Events.styles';

const NO_COUNTIES: string[] = [];

const Events = () => {
  const data = useContext(NafLocationsAppContext);
  const { seoConfig } = data || {};
  const url = useWindowLocation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { postalCode, mc, fylke } = getURLParams() as { postalCode?: string; mc?: string; fylke?: string };
  const [postalNumberInputState, setPostalNumber] = React.useState<string | undefined>(postalCode || undefined);

  useEffect(() => {
    // fetch search results on mount and when postalCode changes in Url
    dispatch(actions.getNafEvents.request({ take: 1000, postalNumber: postalCode || undefined }));
  }, [dispatch, postalCode]);

  const { monthlyEvents, errorState, fetchState, validation } = useSelector((state) => state.nafEvents);

  // Full lists of MC clubs and local-department counties, used for the filter options
  const { nafLocationsByCounty: mcByCounty } = useGetNafLocations(LocationSlugs.mc, NO_COUNTIES, 1000);
  const { nafLocationsByCounty: localByCounty } = useGetNafLocations(LocationSlugs.lokalavdeling, NO_COUNTIES, 1000);

  const selectedMcClubs = mc ? mc.split(',').filter(Boolean) : [];
  const selectedCounties = fylke ? fylke.split(',').filter(Boolean) : [];

  // Map each local-department slug to its county slug, since events only carry the organizer slug
  const deptToCounty = useMemo(() => {
    const map: Record<string, string> = {};
    localByCounty.forEach(({ county, nafLocations }) => {
      nafLocations.forEach((dept) => {
        map[dept.link.data.slug] = county.slug;
      });
    });
    return map;
  }, [localByCounty]);

  // Count events per MC club slug and per county slug from the loaded (postal-code-filtered) events
  const { mcCountBySlug, countyCountBySlug } = useMemo(() => {
    const mcCounts: Record<string, number> = {};
    const countyCounts: Record<string, number> = {};
    monthlyEvents.forEach(({ events }) =>
      events.forEach(({ organizer }) => {
        if (organizer?.type === 'McClub') {
          mcCounts[organizer.slug] = (mcCounts[organizer.slug] || 0) + 1;
        } else if (organizer?.type === 'LocalDepartment') {
          const countySlug = deptToCounty[organizer.slug];
          if (countySlug) countyCounts[countySlug] = (countyCounts[countySlug] || 0) + 1;
        }
      }),
    );
    return { mcCountBySlug: mcCounts, countyCountBySlug: countyCounts };
  }, [monthlyEvents, deptToCounty]);

  const mcClubItems = useMemo(
    () =>
      mcByCounty
        .flatMap(({ nafLocations }) => nafLocations)
        .map((club) => ({
          value: club.link.data.slug,
          label: club.title,
          count: mcCountBySlug[club.link.data.slug] || 0,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [mcByCounty, mcCountBySlug],
  );

  const countyItems = useMemo(
    () =>
      localByCounty
        .map(({ county }) => ({
          value: county.slug,
          label: county.name,
          count: countyCountBySlug[county.slug] || 0,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [localByCounty, countyCountBySlug],
  );

  // Update the url while preserving the other query params (e.g. postalCode)
  const updateQuery = (next: Record<string, string | undefined>) => {
    const current = qs.parse(location.search, { ignoreQueryPrefix: true });
    const merged: Record<string, unknown> = { ...current, ...next };
    Object.keys(merged).forEach((key) => {
      if (!merged[key]) delete merged[key];
    });
    navigate(`${location.pathname}?${qs.stringify(merged)}`);
  };

  const searchWithPostalNumber = () => {
    // Update url with postalcode on return and button press
    updateQuery({ postalCode: postalNumberInputState });
  };

  const handleMcClubsChange = (next: string[]) => updateQuery({ mc: next.length ? next.join(',') : undefined });
  const handleCountiesChange = (next: string[]) => updateQuery({ fylke: next.length ? next.join(',') : undefined });

  // Filter events client-side: an event matches if its MC club OR its county is selected (union).
  // With nothing selected, everything is shown. Months left empty are dropped.
  const hasFilter = selectedMcClubs.length > 0 || selectedCounties.length > 0;
  const filteredMonthlyEvents = !hasFilter
    ? monthlyEvents
    : monthlyEvents
        .map((monthly) => ({
          ...monthly,
          events: monthly.events.filter(({ organizer }) => {
            if (organizer?.type === 'McClub') return selectedMcClubs.includes(organizer.slug);
            if (organizer?.type === 'LocalDepartment') return selectedCounties.includes(deptToCounty[organizer.slug]);
            return false;
          }),
        }))
        .filter((monthly) => monthly.events.length > 0);

  let content: ReactNode | null;

  if (fetchState === 'initial' || (fetchState === 'isUpdating' && monthlyEvents.length === 0)) {
    content = <Spinner />;
  } else if (fetchState === 'error' && !validation) {
    content = <Message error>{errorState?.message}</Message>;
  } else {
    content = (
      <S.Wrapper>
        <S.StyledH4 variant={TextVariant.ArticleTextLargeHeader}>Se hva NAF gjør der du bor</S.StyledH4>
        <S.Layout>
          <EventFilter
            mcClubs={mcClubItems}
            counties={countyItems}
            selectedMcClubs={selectedMcClubs}
            selectedCounties={selectedCounties}
            onMcClubsChange={handleMcClubsChange}
            onCountiesChange={handleCountiesChange}
          />
          <S.Content>
            <S.SearchWrapper>
              <S.Label>
                Ditt postnummer
                <S.StyledInput
                  maxLength={4}
                  id="zip"
                  label="Ditt postnummer"
                  type="number"
                  value={postalNumberInputState}
                  onChange={setPostalNumber}
                  width="auto"
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') searchWithPostalNumber();
                  }}
                />
              </S.Label>
              <S.SearchButton color="secondary" onClick={searchWithPostalNumber}>
                {fetchState === 'isUpdating' ? <Spinner /> : 'Se arrangementer'}
              </S.SearchButton>
            </S.SearchWrapper>
            {validation && <Message error>{validation.message}</Message>}
            {postalCode && fetchState === 'success' && (
              <Message success>{`Viser arrangementer i ${postalCode}`}</Message>
            )}
            {!validation && (
              <EventList
                monthlyEvents={filteredMonthlyEvents}
                noHitText={
                  <>
                    Ser ikke ut som det er noen arrangementer i ditt område fremover. Har du fortsatt lyst til å
                    engasjere deg? Ta kontakt med din lokalavdeling og hør hvordan du kan bidra!
                  </>
                }
                button={<Link to="lokalavdeling" text="Kontakt din lokalavdeling" />}
              />
            )}
          </S.Content>
        </S.Layout>
      </S.Wrapper>
    );
  }

  return (
    <LayoutWithMainContent
      title="Arrangementer"
      description="Liste over NAF sine arrangementer"
      seoDescription="Liste over NAF sine arrangementer"
      isHiddenFromSearch={seoConfig?.isHiddenFromSearch}
      seoTitle="Arrangementer"
      internalSearchMetaData={{ cardType: InternalSearchCardType.Simple }}
      url={url}
    >
      <LocationChips />
      {content}
    </LayoutWithMainContent>
  );
};

export default Events;
